/**
 * PopupWindowService - Manages standalone popup windows for forms
 * 
 * This service enables forms to be "unpinned" from the main application window
 * into separate browser popup windows that can be positioned anywhere on screen.
 * 
 * Features:
 * - Unpin form to standalone popup window
 * - Pin popup back to main application
 * - Communication between main window and popups via postMessage
 * - Track all open popup windows
 * - Cleanup on main window close
 * 
 * Usage:
 *   // From a form:
 *   PopupWindowService.unpinForm('entity', entityData, { width: 1200, height: 800 });
 *   
 *   // From popup to pin back:
 *   PopupWindowService.pinFormBack('entity', entityData);
 */

import { Logger } from './Logger';

const logger = Logger.scope('PopupWindowService');

export interface PopupWindowOptions {
  width?: number;
  height?: number;
  left?: number;
  top?: number;
}

export interface FormState {
  formName: string;
  formData: unknown;
  scrollPosition?: { x: number; y: number };
  activeTab?: string;
  collapsedSections?: string[];
  fieldValues?: Record<string, unknown>;  // Current field values (may differ from formData)
  headerValues?: { forename?: string; surname?: string; company?: string };  // Editable header fields
  focusState?: {
    elementId?: string;          // ID of focused element
    selectionStart?: number;     // Caret position start
    selectionEnd?: number;       // Caret position end (for selections)
    fieldId?: string;            // ID of the field container (for component fields)
  };
}

interface PopupWindow {
  id: string;
  window: Window;
  formName: string;
  formState: FormState;
}

class PopupWindowServiceClass {
  private popups: Map<string, PopupWindow> = new Map();
  private messageHandler: ((event: MessageEvent) => void) | null = null;
  private isPopup: boolean = false;
  private parentOrigin: string = '';

  /**
   * Initialize the service - call once on app startup
   */
  init(): void {
    // Detect if we're running in a popup window
    this.isPopup = window.opener !== null && !window.opener.closed;
    
    if (this.isPopup) {
      this.parentOrigin = document.referrer ? new URL(document.referrer).origin : window.location.origin;
      logger.info('Running in popup mode, parent origin:', this.parentOrigin);
    }

    // Set up message listener for cross-window communication
    this.messageHandler = this.handleMessage.bind(this);
    window.addEventListener('message', this.messageHandler);

    // Clean up popups when main window closes
    if (!this.isPopup) {
      window.addEventListener('beforeunload', () => {
        this.closeAllPopups();
      });
    }

    logger.info('PopupWindowService initialized', { isPopup: this.isPopup });
  }

  /**
   * Check if current window is a popup
   */
  isPopupWindow(): boolean {
    return this.isPopup;
  }

  /**
   * Unpin a form into a standalone popup window
   */
  unpinForm(formName: string, formState: FormState, options: PopupWindowOptions = {}): string | null {
    const {
      width = 1200,
      height = 800,
      left = (window.screen.width - 1200) / 2,
      top = (window.screen.height - 800) / 2
    } = options;

    // Generate unique popup ID
    const popupId = `popup-${formName}-${Date.now()}`;

    // Build the popup URL with form parameters
    const popupUrl = new URL(window.location.href);
    popupUrl.searchParams.set('popup', 'true');
    popupUrl.searchParams.set('popupId', popupId);
    popupUrl.searchParams.set('form', formName);

    // Build window features string
    const features = [
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      'menubar=no',
      'toolbar=no',
      'location=no',
      'status=no',
      'resizable=yes',
      'scrollbars=yes'
    ].join(',');

    // Open the popup window
    const popup = window.open(popupUrl.toString(), popupId, features);

    if (!popup) {
      logger.error('Failed to open popup window - may be blocked by popup blocker');
      return null;
    }

    // Store popup reference
    this.popups.set(popupId, {
      id: popupId,
      window: popup,
      formName,
      formState
    });

    // Send form state to popup once it's loaded
    popup.addEventListener('load', () => {
      this.sendToPopup(popupId, 'FORM_STATE', formState);
    });

    // Also try with a delay as fallback (some browsers don't fire load reliably)
    setTimeout(() => {
      if (popup && !popup.closed) {
        this.sendToPopup(popupId, 'FORM_STATE', formState);
      }
    }, 500);

    // Monitor popup close
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        this.popups.delete(popupId);
        logger.info('Popup closed:', popupId);
      }
    }, 1000);

    logger.info('Opened popup window:', popupId);
    return popupId;
  }

  /**
   * Pin a form back to the main window (called from popup)
   */
  pinFormBack(formState: FormState): void {
    if (!this.isPopup || !window.opener || window.opener.closed) {
      logger.warn('Cannot pin back - not in popup or parent window closed');
      return;
    }

    // Send message to parent window
    window.opener.postMessage({
      type: 'FLEXIVA_PIN_FORM',
      formState
    }, this.parentOrigin || '*');

    // Close this popup
    window.close();
  }

  /**
   * Get the form state passed to this popup window
   */
  getPopupFormState(): FormState | null {
    const urlParams = new URLSearchParams(window.location.search);
    const formName = urlParams.get('form');
    
    if (!formName) return null;

    // Return minimal state from URL - full state comes via postMessage
    return {
      formName,
      formData: null
    };
  }

  /**
   * Request full form state from parent (call after popup initializes)
   */
  requestFormState(): void {
    if (!this.isPopup || !window.opener || window.opener.closed) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const popupId = urlParams.get('popupId');

    window.opener.postMessage({
      type: 'FLEXIVA_REQUEST_STATE',
      popupId
    }, this.parentOrigin || '*');
  }

  /**
   * Close all popup windows
   */
  closeAllPopups(): void {
    for (const [id, popup] of this.popups) {
      if (popup.window && !popup.window.closed) {
        popup.window.close();
      }
    }
    this.popups.clear();
  }

  /**
   * Get count of open popups
   */
  getPopupCount(): number {
    // Clean up closed popups
    for (const [id, popup] of this.popups) {
      if (popup.window.closed) {
        this.popups.delete(id);
      }
    }
    return this.popups.size;
  }

  /**
   * Send message to a specific popup
   */
  private sendToPopup(popupId: string, type: string, data: unknown): void {
    const popup = this.popups.get(popupId);
    if (popup && popup.window && !popup.window.closed) {
      popup.window.postMessage({
        type: `FLEXIVA_${type}`,
        data
      }, window.location.origin);
    }
  }

  /**
   * Handle incoming messages from popups or parent
   */
  private handleMessage(event: MessageEvent): void {
    // Validate origin
    if (event.origin !== window.location.origin && event.origin !== this.parentOrigin) {
      return;
    }

    const { type, data, formState, popupId } = event.data || {};

    switch (type) {
      case 'FLEXIVA_FORM_STATE':
        // Popup received form state from parent
        this.onFormStateReceived(data as FormState);
        break;

      case 'FLEXIVA_PIN_FORM':
        // Parent received pin request from popup
        this.onPinFormRequested(formState as FormState);
        break;

      case 'FLEXIVA_REQUEST_STATE':
        // Parent received state request from popup
        this.onStateRequested(popupId);
        break;
    }
  }

  /**
   * Called when popup receives form state
   */
  private onFormStateReceived(formState: FormState): void {
    logger.info('Received form state in popup:', formState);
    // Dispatch custom event for the form to handle
    window.dispatchEvent(new CustomEvent('flexiva:formstate', { detail: formState }));
  }

  /**
   * Called when parent receives pin request
   */
  private onPinFormRequested(formState: FormState): void {
    logger.info('Pin form requested:', formState);
    // Navigate to the form in the main window
    if (window.OpenForm) {
      window.OpenForm(formState.formName);
      // Dispatch event with state for the form to restore
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('flexiva:restorestate', { detail: formState }));
      }, 500);
    }
  }

  /**
   * Called when popup requests its state
   */
  private onStateRequested(popupId: string): void {
    const popup = this.popups.get(popupId);
    if (popup) {
      this.sendToPopup(popupId, 'FORM_STATE', popup.formState);
    }
  }

  /**
   * Destroy the service
   */
  destroy(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
    }
    this.closeAllPopups();
  }
}

// Export singleton instance
export const PopupWindowService = new PopupWindowServiceClass();
